import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { FileUp, FileText, Bell, Check, Loader2 } from 'lucide-react';

const DocumentNotificationBanner = () => {
  const navigate = useNavigate();
  const [pendingCampaigns, setPendingCampaigns] = useState([]);
  const [unreadDocs, setUnreadDocs] = useState([]);
  const [_dismissedIds, setDismissedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(null);

  useEffect(() => {
    checkNotifications();
  }, []);

  const checkNotifications = async () => {
    try {
      // First get dismissed notification statuses
      const notifRes = await api.get('/notifications?include_dismissed=true');
      const dismissed = new Set();
      (notifRes.data.notifications || []).forEach(n => {
        if (n.status === 'dismissed' && n.type === 'document') {
          dismissed.add(n.reference_id);
        }
      });
      setDismissedIds(dismissed);

      // Check for pending document campaigns
      const campaignRes = await api.get('/document-campaigns/active');
      const pending = (campaignRes.data.campaigns || []).filter(c => !c.has_submitted && !dismissed.has(c.id));
      setPendingCampaigns(pending);

      // Check for unread academy documents
      const docsRes = await api.get('/documents/academy/unread');
      const unread = (docsRes.data.unread_documents || []).filter(d => !dismissed.has(d.id));
      setUnreadDocs(unread);
    } catch (error) {
      console.error('Failed to check document notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markDocumentAsViewed = async (docId) => {
    try {
      await api.post(`/documents/academy/${docId}/view`);
      setUnreadDocs(prev => prev.filter(d => d.id !== docId));
    } catch (error) {
      console.error('Failed to mark document as viewed:', error);
    }
  };

  const handleDismissDocument = async (docId) => {
    setDismissing(`doc_${docId}`);
    try {
      await api.post(`/notifications/document/${docId}/dismiss`);
      setUnreadDocs(prev => prev.filter(d => d.id !== docId));
      setDismissedIds(prev => new Set([...prev, docId]));
    } catch (error) {
      console.error('Failed to dismiss document:', error);
    } finally {
      setDismissing(null);
    }
  };

  const handleDismissCampaign = async (campaignId) => {
    setDismissing(`campaign_${campaignId}`);
    try {
      await api.post(`/notifications/document/${campaignId}/dismiss`);
      setPendingCampaigns(prev => prev.filter(c => c.id !== campaignId));
      setDismissedIds(prev => new Set([...prev, campaignId]));
    } catch (error) {
      console.error('Failed to dismiss campaign:', error);
    } finally {
      setDismissing(null);
    }
  };

  if (loading) return null;
  if (pendingCampaigns.length === 0 && unreadDocs.length === 0) return null;

  return (
    <div className="space-y-3 mb-6" data-testid="document-notification-banner">
      {/* Pending Document Campaigns - Show each as a separate card */}
      {pendingCampaigns.map(campaign => (
        <Card key={campaign.id} className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-amber-100 rounded-full">
                  <FileUp className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-amber-900">
                    Document Upload Required: {campaign.title}
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Please upload the requested documents
                  </p>
                  {campaign.deadline && (
                    <p className="text-xs text-amber-600 mt-1">
                      Deadline: {new Date(campaign.deadline).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => navigate('/parent/document-upload')}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Upload
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDismissCampaign(campaign.id)}
                  disabled={dismissing === `campaign_${campaign.id}`}
                  className="border-green-300 text-green-700 hover:bg-green-100"
                >
                  {dismissing === `campaign_${campaign.id}` ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Reviewed
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Unread Academy Documents */}
      {unreadDocs.map(doc => (
        <Card key={doc.id} className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Bell className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-blue-900">
                    New Document: {doc.title}
                  </h4>
                  <p className="text-sm text-blue-700 mt-1">
                    {doc.category && <Badge variant="outline" className="mr-2">{doc.category}</Badge>}
                    Please review this document
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    markDocumentAsViewed(doc.id);
                    navigate('/parent/academy-documents');
                  }}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  View
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDismissDocument(doc.id)}
                  disabled={dismissing === `doc_${doc.id}`}
                  className="border-green-300 text-green-700 hover:bg-green-100"
                >
                  {dismissing === `doc_${doc.id}` ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Reviewed
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DocumentNotificationBanner;
